import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import './styles.css';
import PendingRequests from './components/PendingRequests';
import UserManagement from './components/UserManagement';
import Leaves from './components/Leaves';
import MyLeaves from './components/MyLeaves';
import MyDashboard from './components/MyDashboard';
import MyAttendance from './components/MyAttendance';
import Notifications from './components/Notifications';
import Attendance from './components/Attendance';
import EmployeeAttendanceSummary from './components/EmployeeAttendanceSummary';
import EmployeeTest from './components/EmployeeTest';
import DebugEmployee from './components/DebugEmployee';
import { onPendingUsersChange, onApprovedEmployeesChange, normalizeHeadDepartmentsFromUser, leaveNeedsDepartmentHeadAction } from './services/userDb';
import { onLeavesChange, updateLeave as dbUpdateLeave } from './services/db';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { onAllAttendanceChange, fetchDailyPresentCountsForRange, fetchAttendanceRowsForRangeFast, toDate, prefetchAttendanceEmployeeCache } from './services/attendanceDb';
import { canonicalDepartmentLabel } from './services/departmentsDb';
import {
  readAppSessionCache,
  writeAppSessionCache,
  clearAllAppSessionCaches,
  readHrTrendCache,
  writeHrTrendCache,
  readHrMonthlyAttendanceCache,
  writeHrMonthlyAttendanceCache,
} from './utils/appSessionCache';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);
import { 
  LayoutDashboard, 
  UserCheck, 
  Users, 
  CalendarRange, 
  ClipboardList, 
  Bell, 
  Home, 
  User, 
  LogOut, 
  Search, 
  Plus, 
  MapPin,
  Clock,
  CheckCircle2,
  AlertCircle,
  Menu,
  X,
  Trash2, 
  CalendarPlus, 
  CheckCircle, 
  XCircle, 
  Eye,
  Building2,
  Shield,
} from 'lucide-react';
import DepartmentSettings from './components/DepartmentSettings.js';
import TeamLeaves from './components/TeamLeaves.js';
import AdminAccessSettings from './components/AdminAccessSettings.js';

// Data

const COLORS = ['#3b5bdb', '#6d28d9', '#2a9d4f', '#d97706', '#c0392b', '#1a6db3', '#6b4f9e'];
const CBGS = ['#eef2ff', '#f5f3ff', '#edfaf2', '#fffbeb', '#fef2f2', '#e6f3fc', '#f0edfb'];

function ec(i) { return COLORS[i % COLORS.length]; }
function ecb(i) { return CBGS[i % CBGS.length]; }

function getCurrentMonthBounds() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (date) => {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  return { monthStart: fmt(start), monthEnd: fmt(end) };
}

const initialEmployees = [];
const initialLeaves = [];
const initialNotifs = [];

// Navigation
const hrNav = [
  { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> },
  { sec: 'Access Control' },
  { id: 'pending-requests', label: 'Pending Approvals', icon: <UserCheck size={18} /> },
  { sec: 'People' },
  { id: 'employees', label: 'Employees', icon: <Users size={18} /> },
  { id: 'employee-attendance', label: 'Employee Summary', icon: <CalendarRange size={18} /> },
  { id: 'attendance', label: 'Attendance', icon: <MapPin size={18} /> },
  { sec: 'Leave' },
  { id: 'leaves', label: 'Leave Requests', icon: <ClipboardList size={18} /> },
  { sec: 'System' },
  { id: 'departments', label: 'Departments', icon: <Building2 size={18} /> },
  { id: 'admin-access', label: 'Admin access', icon: <Shield size={18} /> },
  { id: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
];

const empNav = [
  { id: 'my-dashboard', label: 'My Dashboard', icon: <Home size={18} /> },
  { sec: 'Work' },
  { id: 'my-attendance', label: 'Attendance', icon: <MapPin size={18} /> },
  { id: 'team-leaves', label: 'Team Leaves', icon: <UserCheck size={18} /> },
  { id: 'my-leaves', label: 'My Leaves', icon: <ClipboardList size={18} /> },
  { sec: 'Other' },
  { id: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
  { id: 'profile', label: 'My Profile', icon: <User size={18} /> },
];

// Toast Component
function Toast({ toast, onRemove }) {
  useEffect(() => {
    const timer = setTimeout(() => onRemove(toast.id), 3500);
    return () => clearTimeout(timer);
  }, [toast.id, onRemove]);

  const icon = toast.type === 'ok' ? '' : toast.type === 'err' ? '' : '';
  return (
    <div className={`toast t${toast.type}`}>
      {icon} {toast.message}
    </div>
  );
}

// Modal Component
function Modal({ isOpen, title, children, footer, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="ov open" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="mh">
          <h2>{title}</h2>
          <button className="mc" onClick={onClose}>×</button>
        </div>
        <div className="mb">{children}</div>
        <div className="mf">{footer}</div>
      </div>
    </div>
  );
}

// Sidebar Component
// Pending Approval Screen
function PendingApproval({ currentUser, logout }) {
  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      background: '#f0f2fb',
      padding: '24px',
      textAlign: 'center'
    }}>
      <div className="card" style={{ maxWidth: '400px', padding: '40px' }}>
        <div style={{ 
          width: '64px', 
          height: '64px', 
          background: 'rgba(79, 70, 229, 0.1)', 
          borderRadius: '50%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: '#4f46e5',
          margin: '0 auto 24px'
        }}>
          <Clock size={32} />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '12px' }}>Approval Pending</h2>
        <p style={{ color: 'var(--text2)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
          Hey <strong>{currentUser?.name || 'there'}</strong>, your access request for the <strong>{currentUser?.department?.trim() ? canonicalDepartmentLabel(currentUser.department) : 'selected'}</strong> department is waiting for HR approval.
        </p>
        <div style={{ 
          background: 'var(--bg-sec)', 
          padding: '16px', 
          borderRadius: '8px', 
          fontSize: '12px', 
          color: 'var(--text3)',
          marginBottom: '24px'
        }}>
          💡 This page will <strong>automatically refresh</strong> once you are approved. You don't need to do anything!
        </div>
        <button className="btn btn-g" onClick={logout} style={{ width: '100%', justifyContent: 'center' }}>
          Cancel & Log Out
        </button>
      </div>
    </div>
  );
}

function Sidebar({ role, page, onNavigate, currentUser, notifications, leaves, pendingUsersCount, isOpen, onClose, onLogout, employees }) {
  const navItems =
    role === 'hr'
      ? hrNav
      : empNav.filter((item) => {
          if (item.id === 'team-leaves') return normalizeHeadDepartmentsFromUser(currentUser).length > 0;
          return true;
        });
  const teamHeadBadge =
    role === 'emp' && currentUser.email
      ? leaves.filter((l) => leaveNeedsDepartmentHeadAction(l, currentUser, employees)).length
      : 0;
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="logo" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className="logo-mark">A</div>
          <div className="logo-text">Acad<span>XL</span></div>
        </div>
        <button className="mc" onClick={onClose} style={{ border: 'none', background: 'none', color: 'var(--text)', display: 'none' /* Will show via CSS */ }}>
          <X size={20} />
        </button>
      </div>
      
      <div className="role-pill">
        <div className="rp-lbl">{role === 'hr' ? 'HR Admin' : 'Employee'}</div>
        <div className="rp-nm">{currentUser.name}</div>
      </div>
      <nav className="nav">
        {navItems.map((item, index) => {
          if (item.sec) {
            return <div key={index} className="ns">{item.sec}</div>;
          }
          const badge = item.id === 'leaves' && role === 'hr' ? 
            notifications.filter(n => n.type === 'leave' && !n.read).length || leaves.filter(l => l.status === 'pending' || l.status === 'pending_head').length : 0;
          const userBadge = item.id === 'pending-requests' && role === 'hr' ?
            (typeof pendingUsersCount !== 'undefined' ? pendingUsersCount : 0) : 0;
          return (
            <div
              key={item.id}
              className={`ni ${page === item.id ? 'active' : ''}`}
              onClick={() => {
                onNavigate(item.id);
                if (window.innerWidth <= 768) onClose();
              }}
            >
              <span className="ic">{item.icon}</span>
              <span>{item.label}</span>
              {badge > 0 && <span className="nbadge">{badge}</span>}
              {userBadge > 0 && <span className="nbadge" style={{background: 'var(--amber)'}}>{userBadge}</span>}
              {item.id === 'team-leaves' && teamHeadBadge > 0 && (
                <span className="nbadge" style={{ background: 'var(--purple)' }}>{teamHeadBadge}</span>
              )}
            </div>
          );
        })}
      </nav>
      <div className="sidebar-foot">
        <div className="umini">
          <div className="av" style={{ background: 'var(--accent)', color: 'white' }}>{currentUser.ini}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="un" style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{currentUser.name}</div>
            <div className="ur">{currentUser.role}</div>
          </div>
          <div 
            onClick={() => onLogout()} 
            style={{ 
              cursor: 'pointer', 
              padding: '8px', 
              borderRadius: '6px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              transition: 'background 0.2s',
              color: 'var(--text2)'
            }}
            title="Logout"
            className="logout-btn-hover"
          >
            <LogOut size={20} />
          </div>
        </div>
      </div>
    </aside>
  );
}

// Topbar Component
function Topbar({ title, onSearch, role, notifications, onLogout, onNavigate, onMenuClick }) {
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="topbar">
      <button className="tb-menu-btn" onClick={onMenuClick}>
        <Menu size={20} />
      </button>
      <div className="tb-title">{title}</div>
      {role === 'hr' && (
        <div className="tb-search-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', color: 'var(--text3)' }} />
          <input
            className="tb-search"
            placeholder="Search employees..."
            onChange={onSearch}
            style={{ paddingLeft: '32px' }}
          />
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginLeft: 'auto' }}>
        <div className="tb-btn" title="Notifications" onClick={() => onNavigate && onNavigate('notifications')}>
          <Bell size={20} />
          {unreadCount > 0 && <div className="ndot"></div>}
        </div>
      </div>
    </div>
  );
}

const chartAxisOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
};

// Dashboard Component
function Dashboard({ employees, leaves, attendanceSummary, attendanceData = [], pendingUsers, setLeaves, notifications, setNotifications, showToast, openModal, navigate }) {
  const pendingLeaves = leaves.filter(
    (l) => l.status === 'pending' || l.status === 'pending_head'
  ).length;
  const [insightFilter, setInsightFilter] = useState(null);

  const todayStr = useMemo(() => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(), []);

  const leavesOnLeaveToday = useMemo(() => {
    return leaves.filter((l) => {
      if (l.status !== 'approved') return false;
      const from = l.from;
      const to = l.to;
      if (!from || !to) return false;
      return todayStr >= from && todayStr <= to;
    });
  }, [leaves, todayStr]);

  const presentRecords = useMemo(
    () =>
      attendanceData.filter(
        (a) =>
          a.status === 'IN' ||
          a.status === 'OUT' ||
          a.status === 'AUTO_CHECKOUT'
      ),
    [attendanceData]
  );

  const absentRecords = useMemo(
    () => attendanceData.filter((a) => a.status === 'ABSENT'),
    [attendanceData]
  );

  const toggleInsight = (key) => {
    setInsightFilter((prev) => (prev === key ? null : key));
  };

  const insightCardStyle = (key) => ({
    cursor: 'pointer',
    transition: 'box-shadow 0.15s, transform 0.12s',
    boxShadow:
      insightFilter === key
        ? '0 0 0 2px var(--accent, #3b5bdb), 0 6px 20px rgba(59, 91, 219, 0.12)'
        : undefined,
  });

  const fmtTime = (v) => {
    const d = toDate(v);
    return d
      ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
      : '—';
  };
  const { trendDayKeys, trendLabels } = useMemo(() => {
    const keys = [];
    const labels = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      keys.push(d.toISOString().split('T')[0]);
      labels.push(d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }));
    }
    return { trendDayKeys: keys, trendLabels: labels };
  }, []);

  const [trendCounts, setTrendCounts] = useState(() => new Array(30).fill(0));

  useEffect(() => {
    const startStr = trendDayKeys[0];
    const endStr = trendDayKeys[trendDayKeys.length - 1];
    let cancelled = false;

    const applyCounts = (countsByDate) => {
      setTrendCounts(trendDayKeys.map((ds) => countsByDate[ds] || 0));
    };

    const cached = readHrTrendCache(startStr, endStr);
    if (cached) {
      applyCounts(cached);
    }

    const run = async () => {
      try {
        const countsByDate = await fetchDailyPresentCountsForRange(startStr, endStr);
        if (cancelled) return;
        applyCounts(countsByDate);
        writeHrTrendCache(startStr, endStr, countsByDate);
      } catch (e) {
        console.error('Dashboard attendance trend:', e);
      }
    };

    const t = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) void run();
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(t);
    };
  }, [trendDayKeys]);

  // Summary card uses live listener; graph uses fetchDailyPresentCountsForRange. If the listener
  // errors (e.g. index) or lags, the card showed 0 while the graph showed correct counts — align them.
  const totalEmpForAttendance = useMemo(
    () => Math.max(attendanceSummary.totalEmployees || 0, employees.length || 0),
    [attendanceSummary.totalEmployees, employees.length]
  );
  const presentTodayDisplay = useMemo(() => {
    const idx = trendDayKeys.length - 1;
    const trendP = idx >= 0 ? trendCounts[idx] ?? 0 : 0;
    return Math.max(attendanceSummary.present, trendP);
  }, [trendDayKeys, trendCounts, attendanceSummary.present]);
  const absentTodayDisplay = useMemo(
    () => Math.max(0, totalEmpForAttendance - presentTodayDisplay),
    [totalEmpForAttendance, presentTodayDisplay]
  );

  useEffect(() => {
    const idx = trendDayKeys.length - 1;
    if (idx < 0) return;
    setTrendCounts((prev) => {
      const cur = prev[idx] ?? 0;
      if (attendanceSummary.present <= cur) return prev;
      const next = [...prev];
      next[idx] = attendanceSummary.present;
      return next;
    });
  }, [attendanceSummary.present, trendDayKeys]);

  const trendChartData = useMemo(
    () => ({
      labels: trendLabels,
      datasets: [
        {
          label: 'Present',
          data: trendCounts,
          borderColor: 'rgb(59, 91, 219)',
          backgroundColor: 'rgba(59, 91, 219, 0.12)',
          fill: true,
          tension: 0.35,
          pointRadius: 2,
          pointHoverRadius: 4,
        },
      ],
    }),
    [trendLabels, trendCounts]
  );

  const trendChartOptions = useMemo(
    () => ({
      ...chartAxisOpts,
      animation: { duration: 0 },
      responsiveAnimationDuration: 0,
      scales: {
        x: {
          ticks: { maxRotation: 45, minRotation: 0, font: { size: 10 } },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, stepSize: 1 },
        },
      },
    }),
    []
  );

  const deptChartData = useMemo(() => {
    const counts = {};
    employees.forEach((e) => {
      const dept = canonicalDepartmentLabel(e.department);
      counts[dept] = (counts[dept] || 0) + 1;
    });
    const keys = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
    return {
      labels: keys,
      datasets: [
        {
          label: 'Headcount',
          data: keys.map((k) => counts[k]),
          backgroundColor: keys.map(
            (_, i) => `hsla(${210 + (i * 37) % 120}, 62%, 52%, 0.88)`
          ),
          borderRadius: 6,
        },
      ],
    };
  }, [employees]);

  const deptChartOptions = useMemo(
    () => ({
      ...chartAxisOpts,
      indexAxis: 'y',
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0, stepSize: 1 } },
        y: {
          grid: { display: false },
          ticks: {
            autoSkip: false,
            font: { size: 11 },
          },
        },
      },
    }),
    []
  );

  /** Shared plot height for the dashboard row (dept bars + 30-day line) so both cards align visually. */
  const dashboardChartsRowHeightPx = useMemo(
    () => Math.min(520, Math.max(220, deptChartData.labels.length * 26 + 56)),
    [deptChartData.labels.length]
  );

  const approveLeave = (id) => {
    const leave = leaves.find(l => l.id === id);
    if (!leave) return;

    const updatedLeaves = leaves.map(l => 
      l.id === id ? { ...l, status: 'approved' } : l
    );
    dbUpdateLeave(id, {status: 'approved'});
    setLeaves(updatedLeaves);

    const newNotification = {
      id: Date.now(),
      type: 'leave',
      icon: '✓',
      title: 'Leave approved',
      body: `${leave.emp}'s ${leave.type} (${leave.days} days) approved`,
      time: 'Just now',
      read: false
    };
    setNotifications([newNotification, ...notifications]);

    showToast(`${leave.emp}'s leave approved!`, 'ok');
  };

  const rejectModal = (id) => {
    const leave = leaves.find(l => l.id === id);
    openModal({
      isOpen: true,
      title: 'Reject Leave',
      content: (
        <>
          <p style={{ fontSize: '13px', color: 'var(--text2)', marginBottom: '12px' }}>
            Rejecting <strong style={{ color: 'var(--text)' }}>{leave.emp}</strong>'s {leave.type} ({leave.days} days)
          </p>
          <div className="fg">
            <label>Rejection reason *</label>
            <textarea id="rr" rows="3" placeholder="Give a clear reason so the employee understands..."></textarea>
          </div>
        </>
      ),
      footer: (
        <>
          <button className="btn btn-g" onClick={() => openModal({ isOpen: false })}>Cancel</button>
          <button className="btn btn-ng" onClick={() => doReject(id)}>✗ Reject & Notify</button>
        </>
      )
    });
  };

  const doReject = (id) => {
    const reason = document.getElementById('rr')?.value?.trim();
    if (!reason) {
      showToast('Please provide a reason', 'err');
      return;
    }

    const leave = leaves.find(l => l.id === id);
    const updatedLeaves = leaves.map(l => 
      l.id === id ? { ...l, status: 'rejected' } : l
    );
    setLeaves(updatedLeaves);

    const newNotification = {
      id: Date.now(),
      type: 'leave',
      icon: '✓',
      title: 'Leave rejected',
      body: `${leave.emp}'s leave rejected: ${reason}`,
      time: 'Just now',
      read: false
    };
    setNotifications([newNotification, ...notifications]);

    dbUpdateLeave(id, {status: 'rejected'});
    openModal({ isOpen: false });
    showToast(`${leave.emp}'s leave rejected.`, 'inf');
  };

  return (
    <div className="pg">
      <div className="ph">
        <div>
          <h1>Good morning, Admin 👋</h1>
          <p>{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
      </div>
      <div className="sg">
        <div
          className="sc bl"
          style={insightCardStyle('total')}
          onClick={() => toggleInsight('total')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleInsight('total');
            }
          }}
          role="button"
          tabIndex={0}
          aria-pressed={insightFilter === 'total'}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="sl">Total Employees</div>
              <div className="sv">{employees.length}</div>
            </div>
            <div className="ic-box bl"><Users size={20} /></div>
          </div>
          <div className="ss">Active in system · click to list</div>
        </div>
        <div
          className="sc gn"
          style={insightCardStyle('present')}
          onClick={() => toggleInsight('present')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleInsight('present');
            }
          }}
          role="button"
          tabIndex={0}
          aria-pressed={insightFilter === 'present'}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="sl">Present Today</div>
              <div className="sv">{presentTodayDisplay}</div>
            </div>
            <div className="ic-box gn"><UserCheck size={20} /></div>
          </div>
          <div className="ss">
            {totalEmpForAttendance > 0 ? 
              `${Math.round((presentTodayDisplay / totalEmpForAttendance) * 100)}% attendance rate` : 
              'No attendance data yet'}
          </div>
        </div>
        <div
          className="sc am"
          style={insightCardStyle('leave')}
          onClick={() => toggleInsight('leave')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleInsight('leave');
            }
          }}
          role="button"
          tabIndex={0}
          aria-pressed={insightFilter === 'leave'}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="sl">On Leave</div>
              <div className="sv">{leavesOnLeaveToday.length}</div>
            </div>
            <div className="ic-box am"><CalendarRange size={20} /></div>
          </div>
          <div className="ss">Approved leave · today</div>
        </div>
        <div
          className="sc rd"
          style={insightCardStyle('absent')}
          onClick={() => toggleInsight('absent')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleInsight('absent');
            }
          }}
          role="button"
          tabIndex={0}
          aria-pressed={insightFilter === 'absent'}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="sl">Absent Today</div>
              <div className="sv">{absentTodayDisplay}</div>
            </div>
            <div className="ic-box rd"><Clock size={20} /></div>
          </div>
          <div className="ss">Not checked in yet</div>
        </div>
      </div>

      {insightFilter && (
        <div className="card" style={{ marginBottom: '20px', padding: 0, overflow: 'hidden' }}>
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--bg-sec)',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            <div style={{ fontWeight: '700', fontSize: '14px' }}>
              {insightFilter === 'total' && `All employees (${employees.length})`}
              {insightFilter === 'present' && `Present today (${presentTodayDisplay})`}
              {insightFilter === 'leave' && `On leave today (${leavesOnLeaveToday.length})`}
              {insightFilter === 'absent' && `Absent today (${absentRecords.length})`}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {(insightFilter === 'present' || insightFilter === 'absent') && (
                <button type="button" className="btn btn-p" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => navigate('attendance')}>
                  Open attendance
                </button>
              )}
              {insightFilter === 'leave' && (
                <button type="button" className="btn btn-p" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => navigate('leaves')}>
                  Open leaves
                </button>
              )}
              <button type="button" className="btn btn-g" style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={() => setInsightFilter(null)}>
                <X size={16} /> Close
              </button>
            </div>
          </div>
          <div style={{ maxHeight: 'min(380px, 55vh)', overflow: 'auto' }}>
            {insightFilter === 'total' && (
              employees.length === 0 ? (
                <div style={{ padding: '28px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>No employees in the system.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Name</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Email</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Department</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp, i) => (
                      <tr key={emp.id || emp.email || i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px', fontWeight: '600' }}>{emp.name || (emp.email || '').split('@')[0]}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text2)' }}>{emp.email || emp.id}</td>
                        <td style={{ padding: '12px 16px' }}>{emp.department?.trim() ? canonicalDepartmentLabel(emp.department) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
            {insightFilter === 'present' && (
              presentRecords.length === 0 ? (
                <div style={{ padding: '28px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>No one marked present yet today.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Name</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Department</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Check-in</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {presentRecords.map((row) => (
                      <tr key={row.id || row.email} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: '600' }}>{row.userName}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{row.email}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>{row.department?.trim() ? canonicalDepartmentLabel(row.department) : '—'}</td>
                        <td style={{ padding: '12px 16px' }}>{fmtTime(row.checkIn)}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--accent)' }}>{row.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
            {insightFilter === 'absent' && (
              absentRecords.length === 0 ? (
                <div style={{ padding: '28px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>No absent records for today.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Name</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Department</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absentRecords.map((row) => (
                      <tr key={row.id || row.email} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: '600' }}>{row.userName}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{row.email}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>{row.department?.trim() ? canonicalDepartmentLabel(row.department) : '—'}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text2)', fontSize: '12px' }}>No check-in today</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
            {insightFilter === 'leave' && (
              leavesOnLeaveToday.length === 0 ? (
                <div style={{ padding: '28px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>No approved leave covering today.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Employee</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Type</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Dates</th>
                      <th style={{ padding: '12px 16px', color: 'var(--text2)', fontWeight: '600' }}>Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leavesOnLeaveToday.map((lv) => (
                      <tr key={lv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: '600' }}>{lv.emp}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{lv.eEmail}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>{lv.type}</td>
                        <td style={{ padding: '12px 16px', fontSize: '12px' }}>{lv.from} → {lv.to}</td>
                        <td style={{ padding: '12px 16px' }}>{lv.days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>
      )}
      <div className="g21">
        <div className="card">
          <div className="ct">Attendance trend - Last 30 days</div>
          <div style={{ height: `${dashboardChartsRowHeightPx}px`, position: 'relative' }}>
            {employees.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text3)', fontSize: '13px', textAlign: 'center', padding: '12px' }}>
                No employees yet — add approved users to see attendance trends.
              </div>
            ) : (
              <Line data={trendChartData} options={trendChartOptions} />
            )}
          </div>
        </div>
        <div className="card">
          <div className="ct">Dept headcount</div>
          <div style={{ height: `${dashboardChartsRowHeightPx}px`, position: 'relative' }}>
            {employees.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text3)', fontSize: '13px' }}>
                No department data yet
              </div>
            ) : (
              <Bar data={deptChartData} options={deptChartOptions} />
            )}
          </div>
        </div>
      </div>
      <div className="g2">
        <div className="card">
          <div className="ct">Pending leave requests {pendingLeaves > 0 && <span className="pbadge pulse">{pendingLeaves} Action Required</span>}</div>
          {leaves.filter((l) => l.status === 'pending' || l.status === 'pending_head').slice(0, 4).map((leave, i) => (
            <div key={leave.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderBottom: '1px solid var(--border)', background: 'var(--amber-bg)', borderRadius: 'var(--r-sm)', marginBottom: '8px' }}>
              <div className="av" style={{ background: ecb(i), color: ec(i), width: '32px', height: '32px', fontSize: '11px' }}>
                {leave.ini}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '600', fontSize: '13px' }}>{leave.emp}</div>
                <div style={{ fontSize: '11px', color: 'var(--text2)' }}>{leave.type} · {leave.days} day{leave.days > 1 ? 's' : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button className="btn btn-ok btn-sm" onClick={() => approveLeave(leave.id)}>✓</button>
                <button className="btn btn-ng btn-sm" onClick={() => rejectModal(leave.id)}>✗</button>
              </div>
            </div>
          ))}
          {pendingLeaves === 0 && (
            <div style={{ textAlign: 'center', padding: '18px', color: 'var(--text3)', fontSize: '13px' }}>
              ✅ No pending requests
            </div>
          )}
        </div>

        <div className="card">
          <div className="ct">New User Approvals {pendingUsers.length > 0 && <span className="pbadge pulse" style={{background: 'var(--purple)'}}>{pendingUsers.length} New Joiners</span>}</div>
          {pendingUsers.slice(0, 4).map((user, i) => (
            <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderBottom: '1px solid var(--border)', background: 'var(--purple-bg)', borderRadius: 'var(--r-sm)', marginBottom: '8px' }}>
              <div className="av" style={{ background: 'white', color: 'var(--purple)', width: '32px', height: '32px', fontSize: '11px', border: '1px solid var(--purple)' }}>
                {(user.name || user.email || 'U').substring(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '600', fontSize: '13px' }}>{user.name || 'Anonymous User'}</div>
                <div style={{ fontSize: '11px', color: 'var(--text2)' }}>{user.email} · {user.department?.trim() ? canonicalDepartmentLabel(user.department) : 'No Dept Selected'}</div>
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button className="btn btn-p btn-sm" style={{background: 'var(--purple)', color: 'white'}} onClick={() => navigate('pending-requests')}>View & Approve</button>
              </div>
            </div>
          ))}
          {pendingUsers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '18px', color: 'var(--text3)', fontSize: '13px' }}>
              👥 All users registered
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Dashboard App Component
function DashboardApp({ userRole }) {
  const { currentUser, logout } = useAuth();
  const role = userRole.toLowerCase();
  const sessionEmail = currentUser?.email;
  const cachedSession = sessionEmail ? readAppSessionCache(sessionEmail) : null;

  const [page, setPage] = useState(role === 'hr' ? 'dashboard' : 'my-dashboard');
  const [employees, setEmployees] = useState(() => cachedSession?.employees ?? initialEmployees);
  const [leaves, setLeaves] = useState(() => cachedSession?.leaves ?? initialLeaves);
  const [notifications, setNotifications] = useState(() => cachedSession?.notifications ?? initialNotifs);
  const [toasts, setToasts] = useState([]);
  const [modal, setModal] = useState({ isOpen: false, title: '', content: '', footer: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingUsers, setPendingUsers] = useState(() => cachedSession?.pendingUsers ?? []);

  const [attendanceSummary, setAttendanceSummary] = useState(
    () =>
      cachedSession?.attendanceSummary ?? {
        present: 0,
        absent: 0,
        totalEmployees: 0,
        attendanceRate: 0,
      }
  );
  const [attendanceData, setAttendanceData] = useState(() => cachedSession?.attendanceData ?? []);
  const [employeeAttendanceMonthData, setEmployeeAttendanceMonthData] = useState(
    () => cachedSession?.employeeAttendanceMonthData ?? []
  );

  const cacheWriteTimerRef = useRef(null);
  useEffect(() => {
    if (!sessionEmail) return;
    if (cacheWriteTimerRef.current) clearTimeout(cacheWriteTimerRef.current);
    cacheWriteTimerRef.current = setTimeout(() => {
      writeAppSessionCache(sessionEmail, {
        employees,
        leaves,
        notifications,
        pendingUsers,
        attendanceSummary,
        attendanceData,
        employeeAttendanceMonthData,
      });
    }, 400);
    return () => {
      if (cacheWriteTimerRef.current) clearTimeout(cacheWriteTimerRef.current);
    };
  }, [
    sessionEmail,
    employees,
    leaves,
    notifications,
    pendingUsers,
    attendanceSummary,
    attendanceData,
    employeeAttendanceMonthData,
  ]);

  const handleLogout = useCallback(() => {
    clearAllAppSessionCaches();
    logout();
  }, [logout]);

  useEffect(() => {
    const unsubList = [];

    // Use a stable date format (today)
    const todayStr = new Date().toISOString().split('T')[0];

    if (userRole === 'HR') {
      const { monthStart, monthEnd } = getCurrentMonthBounds();

      unsubList.push(onPendingUsersChange((users) => {
        setPendingUsers(users.filter(u => u.status !== 'REJECTED'));
      }));
      
      unsubList.push(onApprovedEmployeesChange((empData) => {
        setEmployees(empData);
      }));

      void prefetchAttendanceEmployeeCache();
      const cachedMonthRows = readHrMonthlyAttendanceCache(monthStart, monthEnd);
      if (cachedMonthRows?.length) {
        setEmployeeAttendanceMonthData(cachedMonthRows);
      }

      // CENTRALIZED ATTENDANCE LISTENERS (Unified Summary + Records)
      unsubList.push(onAllAttendanceChange(todayStr, (data) => {
        setAttendanceData(data.records);
        setAttendanceSummary(data.summary);
      }));

      void fetchAttendanceRowsForRangeFast(monthStart, monthEnd)
        .then((rows) => {
          setEmployeeAttendanceMonthData(rows);
          writeHrMonthlyAttendanceCache(monthStart, monthEnd, rows);
        })
        .catch((error) => {
          console.error('HR monthly attendance prefetch error:', error);
        });
    }

    // Both HR and Employee might need leave data
    unsubList.push(onLeavesChange((leaveData) => {
      setLeaves(leaveData);
    }));

    return () => {
      unsubList.forEach(unsub => unsub());
    };
  }, [userRole]);

  const appUser = currentUser ? {
    id: currentUser.uid,
    name: currentUser.name || currentUser.email.split('@')[0],
    ini: (currentUser.name || currentUser.email || 'UU').substring(0, 2).toUpperCase(),
    email: currentUser.email,
    role: currentUser.role === 'HR' ? 'HR Administrator' : 'Employee',
    department: currentUser.department || '',
    headOfDepartment: normalizeHeadDepartmentsFromUser(currentUser).join(', ') || '',
    headOfDepartments: normalizeHeadDepartmentsFromUser(currentUser),
    dept: currentUser.role === 'HR' ? 'HR' : (currentUser.department || 'General')
  } : { id: null, name: 'Guest', ini: 'G', email: '', role: 'Guest', dept: '', department: '', headOfDepartment: '', headOfDepartments: [] };

  const pageTitle = {
    dashboard: 'Dashboard',
    employees: 'Employees',
    'employee-attendance': 'Employee Attendance Summary',
    attendance: 'Attendance',
    leaves: 'Leave Requests',
    'my-dashboard': 'My Dashboard',
    'my-attendance': 'My Attendance',
    'my-leaves': 'My Leaves',
    'team-leaves': 'Team Leaves',
    'my-payslip': 'My Payslip',
    profile: 'My Profile',
    notifications: 'Notifications',
    'pending-requests': 'Pending Approvals',
    'user-management': 'System Users',
    departments: 'Departments',
    'admin-access': 'Admin access',
  }[page] || page;

  const showToast = (message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const navigate = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query && role === 'hr') {
      setPage('employees');
    }
  };

  const renderContent = () => {
    switch (page) {
      case 'dashboard':
        return <Dashboard employees={employees} leaves={leaves} attendanceSummary={attendanceSummary} attendanceData={attendanceData} pendingUsers={pendingUsers} setLeaves={setLeaves} notifications={notifications} setNotifications={setNotifications} showToast={showToast} openModal={setModal} navigate={navigate} />;
      case 'employees':
        return <UserManagement showToast={showToast} currentUserEmail={currentUser?.email} />;
      case 'employee-attendance': {
        const { monthStart, monthEnd } = getCurrentMonthBounds();
        return (
          <EmployeeAttendanceSummary
            employees={employees}
            leaves={leaves}
            initialMonthlyData={employeeAttendanceMonthData}
            initialMonthStart={monthStart}
            initialMonthEnd={monthEnd}
            showToast={showToast}
          />
        );
      }
      case 'attendance':
        return <Attendance employees={employees} leaves={leaves} initialData={attendanceData} initialSummary={attendanceSummary} showToast={showToast} />;
      case 'leaves':
        return <Leaves leaves={leaves} setLeaves={setLeaves} employees={employees} notifications={notifications} setNotifications={setNotifications} showToast={showToast} openModal={setModal} />;
      case 'notifications':
        return <Notifications notifications={notifications} setNotifications={setNotifications} role={role} showToast={showToast} />;
      case 'departments':
        return <DepartmentSettings showToast={showToast} />;
      case 'admin-access':
        return <AdminAccessSettings showToast={showToast} />;
      case 'pending-requests':
        return <PendingRequests currentUser={currentUser} showToast={showToast} />;
      case 'my-dashboard':
        return <MyDashboard currentUser={appUser} leaves={leaves} employees={employees} onNavigate={navigate} />;
      case 'my-attendance':
        return <MyAttendance currentUser={appUser} showToast={showToast} />;
      case 'team-leaves':
        return <TeamLeaves currentUser={appUser} leaves={leaves} employees={employees} showToast={showToast} />;
      case 'my-leaves':
        return <MyLeaves currentUser={appUser} leaves={leaves} showToast={showToast} openModal={setModal} />;
      case 'profile':
        return <div>My Profile - Coming Soon</div>;
      default:
        return <div>Page not found</div>;
    }
  };

    return (
      <div className="app">
        {currentUser.role === 'PENDING' && currentUser.status === 'REJECTED' ? (
          <Navigate to="/login" replace />
        ) : currentUser.role === 'PENDING' ? (
          <PendingApproval currentUser={appUser} logout={handleLogout} />
        ) : (
          <>
            <Sidebar
              role={role}
              page={page}
              onNavigate={navigate}
              currentUser={appUser}
              notifications={notifications}
              leaves={leaves}
              employees={employees}
              pendingUsersCount={pendingUsers.length}
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              onLogout={handleLogout}
            />
            <div className="main">
              <Topbar
                title={pageTitle}
                onSearch={handleSearch}
                role={role}
                notifications={notifications}
                onLogout={handleLogout}
                onNavigate={navigate}
                onMenuClick={() => setSidebarOpen(true)}
              />
              <div className="content">
                {renderContent()}
              </div>
            </div>
          </>
        )}
        <Modal
        isOpen={modal.isOpen}
        title={modal.title}
        footer={modal.footer}
        onClose={() => setModal({ ...modal, isOpen: false })}
      >
        {modal.content}
      </Modal>
      <div className="tw-wrap">
        {toasts.map(toast => (
          <Toast key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </div>
  );
}


const ProtectedRoute = ({ children, allowedRoles }) => {
  const { currentUser } = useAuth();
  if (!currentUser) return <Navigate to="/login" />;
  if (currentUser.role === 'PROFILE_LOADING') return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(currentUser.role)) return <Navigate to="/login" />;
  return children;
};

function MainApp() {
  const { currentUser } = useAuth();
  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            (() => {
              if (!currentUser) return <Login />;
              if (currentUser.role === 'PROFILE_LOADING') {
                return (
                  <div
                    style={{
                      minHeight: '100vh',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f4f5f7',
                      padding: 24,
                      textAlign: 'center',
                    }}
                  >
                    <div className="spin" style={{ fontSize: '28px', marginBottom: '12px' }}>⏳</div>
                    <div style={{ fontSize: '17px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                      Loading your profile…
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', maxWidth: 360, lineHeight: 1.5 }}>
                      Syncing with the server. If this takes too long, check your internet or try refreshing the page.
                    </div>
                  </div>
                );
              }
              if (currentUser.role === 'NEED_DEPT') return <Login />;
              if (currentUser.role === 'PENDING' && currentUser.status === 'REJECTED') return <Login />;
              if (currentUser.role === 'HR') return <Navigate to="/admin-dashboard" replace />;
              return <Navigate to="/employee-dashboard" replace />;
            })()
          }
        />
        
        <Route path="/admin-dashboard/*" element={
          <ProtectedRoute allowedRoles={['HR']}>
            <DashboardApp userRole="HR" />
          </ProtectedRoute>
        } />

        <Route path="/employee-dashboard/*" element={
          <ProtectedRoute allowedRoles={['EMPLOYEE', 'PENDING']}>
            <DashboardApp userRole="EMPLOYEE" />
          </ProtectedRoute>
        } />
        
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </Router>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}
